import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  NoSymbolIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { GET_BUNDLE_GROUPS, GET_CATEGORIES, GET_MEAL_DEALS } from '../graphql/product-queries'
import { PUBLISH_PRODUCT_AVAILABILITY } from '../graphql/product-mutations'
import { useProductAvailability } from '../hooks/useProductAvailability'
import type { Product } from '../types/product'
import type { AvailabilityAction, AvailabilityChange, PageScope, SoldOutState } from '../lib/availability'
import {
  ALL_CHANNELS_ENABLED,
  AVAILABILITY_POLL_MS,
  PAGE_SCOPES,
  actionLabel,
  buildChange,
  changeAction,
  isNoOp,
  isSoldOutAnywhere,
  lockedInPlatformsScope,
  ownState,
  platformState,
  stateLabel,
} from '../lib/availability'

// Availability page — select products, choose what happens, publish once (wireframes A–E;
// contract ../API/docs/frontend/pos-product-availability.md; owner decisions 2026-09-12 in
// tasks/product-availability-decisions-api-handover.md).

// A poll already in flight when Publish returns carries the old state and overwrites the fresh
// cache entry, flipping the row back. Published values therefore shield their rows for a fixed
// TTL, pruned on expiry only — not on the first confirming poll, because a stale response can
// still land after it. Trade-off: a change another terminal makes to a row published here shows
// up to this long late.
const SHIELD_TTL_MS = 2 * AVAILABILITY_POLL_MS

type AvailabilityFields = Pick<Product, 'sold_out' | 'sold_out_until' | 'platforms_sold_out' | 'platforms_sold_out_until'>

interface PublishOutcome {
  changes: AvailabilityChange[]
  status: 'OK' | 'FAILED' | 'SKIPPED'
  message: string | null
  notOnPlatforms: Set<number>
}

interface RefusedChanges {
  changes: AvailabilityChange[]
  message: string | null
}

const ACTIONS: AvailabilityAction[] = ['END_OF_DAY', 'UNTIL_FURTHER_NOTICE', 'IN_STOCK']

const COLUMN = 'w-60 xl:w-72 flex-shrink-0'

const joinNames = (names: string[]): string =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`

/** The platform part of a publish HubRise refused — what Retry platforms re-sends. */
const refusedPlatformChanges = (o: PublishOutcome): AvailabilityChange[] =>
  o.status === 'FAILED' ? o.changes.filter(c => c.scopes.includes('PLATFORMS')) : []

const outcomeSummary = (o: PublishOutcome): string => {
  const own = o.changes.some(c => c.scopes.includes('OWN')) ? 'Own systems are live. ' : ''
  if (o.status === 'FAILED') {
    const n = refusedPlatformChanges(o).length
    return `${own}${n === 1 ? 'One change was' : `${n} changes were`} not accepted by the platforms.`
  }
  if (o.status === 'OK') return `${own}Platforms update within a minute.`
  return own.trim() || 'None of these products is on the platforms.'
}

// Components live at module level: declared inside the page they would remount on every
// poll-driven render and swallow taps.

const Checkbox = ({ checked }: { checked: boolean }) => (
  <span
    className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${
      checked ? 'bg-brand-jasper text-white' : 'border-2 border-gray-300 bg-white'
    }`}
  >
    {checked && <CheckIcon className="h-5 w-5" />}
  </span>
)

const FilterChip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className={`h-11 rounded-full px-4 text-sm font-medium ${
      active ? 'bg-gray-900 text-white' : 'border border-gray-200 bg-white text-gray-700'
    }`}
  >
    {children}
  </button>
)

const StateChip = ({ state }: { state: SoldOutState }) => (
  <span
    className={`inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[13px] font-semibold ${
      state.soldOut ? 'bg-gray-800 text-white' : 'bg-green-50 text-green-800'
    }`}
  >
    {state.soldOut ? <NoSymbolIcon className="h-4 w-4" /> : <CheckIcon className="h-4 w-4" />}
    {stateLabel(state)}
  </span>
)

const PendingChip = ({ action }: { action: AvailabilityAction }) => (
  <span className="inline-flex h-8 items-center gap-1.5 rounded-full border border-dashed border-brand-jasper bg-[#fdf2f1] px-2.5 text-[13px] font-semibold text-[#972f22]">
    {action === 'IN_STOCK' ? <CheckIcon className="h-4 w-4" /> : <NoSymbolIcon className="h-4 w-4" />}
    {actionLabel(action)}
  </span>
)

const NotOnPlatformsChip = () => (
  <span className="inline-flex h-8 items-center rounded-full bg-gray-100 px-2.5 text-[13px] font-semibold text-gray-500">
    Not on the platforms
  </span>
)

const OutcomePill = ({ label, tone }: { label: string; tone: 'ok' | 'refused' | 'muted' }) => (
  <span
    className={`inline-flex h-7 items-center gap-1 rounded-full px-2 text-xs font-semibold ${
      tone === 'ok' ? 'bg-green-50 text-green-800' : tone === 'refused' ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-500'
    }`}
  >
    {tone === 'ok' && <CheckIcon className="h-4 w-4" />}
    {tone === 'refused' && <ExclamationTriangleIcon className="h-4 w-4" />}
    {label}
  </span>
)

const PlatformRefusal = ({
  refused,
  nameOf,
  busy,
  onRetry,
  onDismiss,
}: {
  refused: RefusedChanges
  nameOf: (id: number) => string
  busy: boolean
  onRetry: () => void
  onDismiss?: () => void
}) => {
  const stillShown = refused.changes.filter(c => c.sold_out).map(c => nameOf(c.product_id))
  const stillHidden = refused.changes.filter(c => !c.sold_out).map(c => nameOf(c.product_id))
  const what = [
    stillShown.length ? `still show ${joinNames(stillShown)}` : '',
    stillHidden.length ? `still hide ${joinNames(stillHidden)}` : '',
  ]
    .filter(Boolean)
    .join(' and ')

  return (
    <div className="flex items-center gap-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
      <div className="flex-1">
        <strong>Uber Eats and Thuisbezorgd {what}.</strong> The platform update was not accepted. Retry, or check
        the HubRise dashboard.
        {refused.message && <div className="mt-1 text-xs text-amber-700">{refused.message}</div>}
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onRetry}
        className="h-11 flex-shrink-0 rounded-md border border-amber-500 bg-white px-3.5 font-semibold disabled:opacity-50"
      >
        {busy ? 'Sending…' : 'Retry platforms'}
      </button>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="h-11 flex-shrink-0 rounded-md px-3 font-medium text-amber-700 hover:bg-amber-100"
        >
          Dismiss
        </button>
      )}
    </div>
  )
}

/** "in Meal Deal" / "Meal Deal side" per product id — the blast radius of a mark. */
const useMealDealLabels = (): Map<number, string[]> => {
  const apollo = useApolloClient()
  const { data } = useQuery(GET_MEAL_DEALS)
  const [labels, setLabels] = useState<Map<number, string[]>>(new Map())

  useEffect(() => {
    const deals: { id: number; name: string }[] = data?.products ?? []
    if (deals.length === 0) return
    let cancelled = false
    Promise.all(deals.map(d => apollo.query({ query: GET_BUNDLE_GROUPS, variables: { bundleProductId: Number(d.id) } })))
      .then(results => {
        if (cancelled) return
        const next = new Map<number, string[]>()
        results.forEach((result, i) => {
          for (const group of result.data?.bundleGroups ?? []) {
            const groupName = String(group.name || '')
            const label = groupName.toLowerCase().includes('main')
              ? `in ${deals[i].name}`
              : `${deals[i].name} ${groupName.toLowerCase()}`
            for (const option of group.options ?? []) {
              const productId = Number(option.eligible_variant?.product?.id)
              if (!productId) continue
              const list = next.get(productId) ?? []
              if (!list.includes(label)) list.push(label)
              next.set(productId, list)
            }
          }
        })
        setLabels(next)
      })
      // Informational only: the list works without it, and the errorLink already toasted.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [data, apollo])

  return labels
}

const AvailabilityPage = () => {
  const navigate = useNavigate()
  const { products: polledProducts, loading } = useProductAvailability()
  const { data: categoriesData } = useQuery(GET_CATEGORIES, { variables: { filters: { display_in_pos: true } } })
  const mealDealLabels = useMealDealLabels()
  const [publish, { loading: publishing }] = useMutation(PUBLISH_PRODUCT_AVAILABILITY)

  const [scope, setScope] = useState<PageScope>(PAGE_SCOPES[0].id)
  const [categoryId, setCategoryId] = useState<number | null>(null)
  const [onlyOut, setOnlyOut] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // One change per product per publish (API rule) — keyed on product id.
  const [pending, setPending] = useState<Map<number, AvailabilityChange>>(new Map())
  const [shield, setShield] = useState<Record<number, { values: AvailabilityFields; at: number }>>({})
  // Only a publish reveals that a product has no platform presence — there is no read field.
  const [notOnPlatforms, setNotOnPlatforms] = useState<Set<number>>(new Set())
  const [outcome, setOutcome] = useState<PublishOutcome | null>(null)
  const [refused, setRefused] = useState<RefusedChanges | null>(null)

  const scopeOption = PAGE_SCOPES.find(s => s.id === scope) ?? PAGE_SCOPES[0]
  const showOwn = scopeOption.scopes.includes('OWN')

  // Drop shield entries on TTL expiry only (see SHIELD_TTL_MS).
  useEffect(() => {
    const expiries = Object.values(shield).map(s => s.at + SHIELD_TTL_MS - Date.now())
    if (expiries.length === 0) return
    const timer = setTimeout(() => {
      setShield(prev => {
        const now = Date.now()
        const kept = Object.entries(prev).filter(([, s]) => now - s.at < SHIELD_TTL_MS)
        return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept)
      })
    }, Math.max(Math.min(...expiries), 0) + 50)
    return () => clearTimeout(timer)
  }, [shield])

  const products = useMemo(
    () => polledProducts.map(p => (shield[Number(p.id)] ? { ...p, ...shield[Number(p.id)].values } : p)),
    [polledProducts, shield]
  )
  const byId = useMemo(() => new Map(products.map(p => [Number(p.id), p])), [products])
  const nameOf = (id: number) => byId.get(id)?.name ?? `Product ${id}`

  // Chips in POS category order, only for categories that hold a listed product.
  const categoryChips = useMemo(() => {
    const listed = new Map<number, string>()
    for (const p of products) for (const c of p.categories ?? []) listed.set(Number(c.id), c.name)
    const ordered: number[] = (categoriesData?.categories ?? [])
      .map((c: { id: number }) => Number(c.id))
      .filter((id: number) => listed.has(id))
    const rest = [...listed.keys()].filter(id => !ordered.includes(id))
    return [...ordered, ...rest].map(id => ({ id, name: listed.get(id) as string }))
  }, [products, categoriesData])

  const isLocked = (p: Product) => !showOwn && lockedInPlatformsScope(p)
  const isOutInScope = (p: Product) => (showOwn ? isSoldOutAnywhere(p) : !!p.platforms_sold_out)

  const visible = products.filter(
    p =>
      (categoryId === null || (p.categories ?? []).some(c => Number(c.id) === categoryId)) &&
      (!onlyOut || isOutInScope(p))
  )
  const selectable = visible.filter(p => !isLocked(p))
  const allSelected = selectable.length > 0 && selectable.every(p => selected.has(Number(p.id)))

  const toggle = (id: number) =>
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map(p => Number(p.id))))

  const switchScope = (next: PageScope) => {
    setScope(next)
    setSelected(new Set())
  }

  const apply = (action: AvailabilityAction) => {
    setPending(prev => {
      const next = new Map(prev)
      for (const id of selected) {
        const p = byId.get(id)
        if (!p || isLocked(p)) continue
        // A new action replaces the product's pending change; one that changes nothing drops it.
        const change = buildChange(id, scopeOption.scopes, action)
        if (isNoOp(p, change)) next.delete(id)
        else next.set(id, change)
      }
      return next
    })
    setSelected(new Set())
  }

  const discard = () => {
    setPending(new Map())
    setSelected(new Set())
  }

  const send = async (changes: AvailabilityChange[]): Promise<PublishOutcome> => {
    const { data } = await publish({ variables: { changes } })
    const result = data.publishProductAvailability
    const at = Date.now()
    setShield(prev => {
      const next = { ...prev }
      for (const p of result.products) {
        next[Number(p.id)] = {
          at,
          values: {
            sold_out: p.sold_out,
            sold_out_until: p.sold_out_until,
            platforms_sold_out: p.platforms_sold_out,
            platforms_sold_out_until: p.platforms_sold_out_until,
          },
        }
      }
      return next
    })
    const sync = result.platform_sync
    const notOn = new Set<number>((sync.not_on_platforms ?? []).map(Number))
    // A refused push lists nothing, so it must not clear what an accepted one revealed.
    if (sync.status !== 'FAILED') {
      setNotOnPlatforms(prev => {
        const next = new Set(prev)
        for (const c of changes) {
          if (!c.scopes.includes('PLATFORMS')) continue
          if (notOn.has(c.product_id)) next.add(c.product_id)
          else next.delete(c.product_id)
        }
        return next
      })
    }
    return { changes, status: sync.status, message: sync.message ?? null, notOnPlatforms: notOn }
  }

  const handlePublish = async () => {
    if (pending.size === 0 || publishing) return
    const batch = [...pending.values()]
    try {
      const result = await send(batch)
      const batchIds = new Set(batch.map(c => c.product_id))
      const nowRefused = refusedPlatformChanges(result)
      setRefused(prev => {
        // A newer publish for a product supersedes its earlier refused change.
        const kept = (prev?.changes ?? []).filter(c => !batchIds.has(c.product_id))
        const changes = [...kept, ...nowRefused]
        if (changes.length === 0) return null
        return { changes, message: nowRefused.length ? result.message : prev?.message ?? null }
      })
      // Remove only what was sent. The action bar stays usable while a publish is in flight, so
      // a change queued meanwhile — or one that replaced a sent change — waits for the next Publish.
      setPending(prev => {
        const next = new Map(prev)
        for (const c of batch) if (next.get(c.product_id) === c) next.delete(c.product_id)
        return next
      })
      setOutcome(result)
    } catch {
      // The global errorLink already toasted the API's message. The batch was refused as a
      // whole, so the pending changes stay for another try.
    }
  }

  const handleRetry = async () => {
    if (!refused || publishing) return
    try {
      // The same changes, own scope included: re-writing the own mark leaves it as it is, while
      // a platforms-only retry of an All-channels change would contradict "out on own systems
      // means out everywhere".
      const result = await send(refused.changes)
      const stillRefused = refusedPlatformChanges(result)
      setRefused(stillRefused.length ? { changes: stillRefused, message: result.message } : null)
      setOutcome(result)
    } catch {
      // Toasted by the errorLink; the refused changes stay.
    }
  }

  return (
    <div>
      {/* Header — sticks under the layout's top bar so Publish stays in reach on a long list */}
      <div className="sticky top-16 z-30 -mx-4 flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white px-4 py-3 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900">Availability</h1>
            <p className="text-[13px] text-gray-500">{scopeOption.subtitle}</p>
          </div>
        </div>

        {PAGE_SCOPES.length > 1 && (
          <div className="flex gap-1 rounded-[10px] bg-gray-100 p-1">
            {PAGE_SCOPES.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => switchScope(s.id)}
                className={`h-11 rounded-lg px-4 text-[15px] font-semibold ${
                  s.id === scope ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={pending.size === 0 || publishing}
            onClick={discard}
            className="h-11 rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-gray-700 disabled:text-gray-400"
          >
            Discard
          </button>
          <button
            type="button"
            disabled={pending.size === 0 || publishing}
            onClick={handlePublish}
            className="flex h-11 items-center gap-2 rounded-lg bg-brand-jasper px-4 text-sm font-semibold text-white disabled:bg-gray-100 disabled:text-gray-400"
          >
            {publishing ? 'Publishing…' : 'Publish'}
            {pending.size > 0 && (
              <span className="rounded-full bg-white bg-opacity-25 px-2 text-xs font-bold">{pending.size}</span>
            )}
          </button>
        </div>
      </div>

      {/* Filters — touch only, no search */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <FilterChip active={categoryId === null} onClick={() => setCategoryId(null)}>
          All
        </FilterChip>
        {categoryChips.map(c => (
          <FilterChip key={c.id} active={categoryId === c.id} onClick={() => setCategoryId(c.id)}>
            {c.name}
          </FilterChip>
        ))}
        <div className="flex-1" />
        <FilterChip active={onlyOut} onClick={() => setOnlyOut(v => !v)}>
          Only out of stock
        </FilterChip>
      </div>

      {!outcome && refused && (
        <div className="mt-4">
          <PlatformRefusal refused={refused} nameOf={nameOf} busy={publishing} onRetry={handleRetry} onDismiss={() => setRefused(null)} />
        </div>
      )}

      {/* List */}
      <div className="mt-3 flex flex-col gap-2">
        <div className="flex h-11 items-center gap-4 px-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <div className="flex-1">Product</div>
          {showOwn && (
            <div className={COLUMN}>
              Own systems <span className="font-medium normal-case tracking-normal">· POS, kiosk, webshop</span>
            </div>
          )}
          <div className={COLUMN}>
            Platforms <span className="font-medium normal-case tracking-normal">· Uber Eats, Thuisbezorgd</span>
          </div>
          <button
            type="button"
            onClick={toggleAll}
            disabled={selectable.length === 0}
            aria-label={allSelected ? 'Clear selection' : 'Select all'}
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center"
          >
            <Checkbox checked={allSelected} />
          </button>
        </div>

        {loading && products.length === 0 && (
          <div className="py-12 text-center text-sm text-gray-500">Loading products…</div>
        )}
        {!loading && visible.length === 0 && (
          <div className="py-12 text-center text-sm text-gray-500">
            {onlyOut ? 'Nothing is out of stock here.' : 'No products in this category.'}
          </div>
        )}

        {visible.map(p => {
          const id = Number(p.id)
          const change = pending.get(id)
          const ownAction = change && change.scopes.includes('OWN') ? changeAction(change) : null
          const platformAction = change && change.scopes.includes('PLATFORMS') ? changeAction(change) : null
          const locked = isLocked(p)
          const isSelected = selected.has(id)
          const subtitle = [
            p.categories?.[0]?.name,
            p.variants && p.variants.length > 1 ? `${p.variants.length} sizes` : null,
            ...(mealDealLabels.get(id) ?? []),
            locked ? (ALL_CHANNELS_ENABLED ? 'out everywhere — change it under All channels' : 'out everywhere') : null,
          ]
            .filter(Boolean)
            .join(' · ')

          return (
            <button
              key={id}
              type="button"
              disabled={locked}
              onClick={() => toggle(id)}
              className={`flex h-16 w-full items-center gap-4 rounded-lg border px-4 text-left ${
                isSelected
                  ? 'border-[#eea096] bg-[#fdf2f1]'
                  : change
                  ? 'border-brand-jasper bg-white'
                  : locked
                  ? 'border-gray-200 bg-gray-50 opacity-60'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-base font-semibold text-gray-900">{p.name}</div>
                <div className="truncate text-[13px] text-gray-500">{subtitle}</div>
              </div>
              {showOwn && (
                <div className={COLUMN}>
                  {ownAction ? <PendingChip action={ownAction} /> : <StateChip state={ownState(p)} />}
                </div>
              )}
              <div className={COLUMN}>
                {platformAction ? (
                  <PendingChip action={platformAction} />
                ) : notOnPlatforms.has(id) ? (
                  <NotOnPlatformsChip />
                ) : (
                  <StateChip state={platformState(p)} />
                )}
              </div>
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center">
                {locked ? <LockClosedIcon className="h-5 w-5 text-gray-400" /> : <Checkbox checked={isSelected} />}
              </div>
            </button>
          )
        })}
      </div>

      {/* Action bar — sticky at the end of the page flow, so it follows the content column and
          the last rows scroll above it */}
      {selected.size > 0 && (
        <div className="sticky bottom-4 z-30 mt-4 flex min-h-[88px] flex-wrap items-center gap-3 rounded-xl bg-gray-900 px-5 py-3 text-white shadow-2xl">
          <div className="flex w-36 flex-col">
            <span className="text-lg font-semibold">{selected.size} selected</span>
            <span className="text-[13px] text-gray-400">Choose what happens</span>
          </div>
          {ACTIONS.map(action => (
            <button
              key={action}
              type="button"
              onClick={() => apply(action)}
              className={`flex h-14 items-center gap-2 rounded-lg px-5 text-[15px] font-semibold ${
                action === 'IN_STOCK' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              {action === 'IN_STOCK' ? <CheckIcon className="h-4 w-4" /> : <NoSymbolIcon className="h-4 w-4" />}
              {actionLabel(action)}
            </button>
          ))}
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="h-14 rounded-lg border border-gray-600 px-4 text-[15px] font-medium text-gray-300 hover:bg-gray-800"
          >
            Clear
          </button>
        </div>
      )}

      {/* Result sheet (board E) */}
      {outcome && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-[720px] flex-col gap-4 rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[22px] font-semibold text-gray-900">
                  Published · {outcome.changes.length} {outcome.changes.length === 1 ? 'change' : 'changes'}
                </h2>
                <p className="mt-1 text-sm text-gray-500">{outcomeSummary(outcome)}</p>
              </div>
              <button
                type="button"
                onClick={() => setOutcome(null)}
                aria-label="Close"
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex min-h-0 flex-col gap-2 overflow-y-auto">
              {outcome.changes.map(c => (
                <div key={c.product_id} className="flex min-h-[56px] items-center gap-4 rounded-lg border border-gray-200 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-semibold text-gray-900">{nameOf(c.product_id)}</div>
                    <div className="text-[13px] text-gray-500">
                      {actionLabel(changeAction(c))} · {c.scopes.includes('OWN') ? 'all channels' : 'platforms'}
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {c.scopes.includes('OWN') && <OutcomePill label="Own systems" tone="ok" />}
                    {c.scopes.includes('PLATFORMS') &&
                      (outcome.notOnPlatforms.has(c.product_id) ? (
                        <OutcomePill label="Not on the platforms" tone="muted" />
                      ) : (
                        <OutcomePill label="Platforms" tone={outcome.status === 'FAILED' ? 'refused' : 'ok'} />
                      ))}
                  </div>
                </div>
              ))}
            </div>

            {outcome.status === 'FAILED' && refused && (
              <PlatformRefusal refused={refused} nameOf={nameOf} busy={publishing} onRetry={handleRetry} />
            )}

            <button
              type="button"
              onClick={() => setOutcome(null)}
              className="h-14 rounded-lg bg-brand-jasper text-base font-semibold text-white"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AvailabilityPage
